import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import BlockShell from "./BlockShell";
import Lightbox from "../Lightbox";
import { absUrl, embedSrcFrom } from "./media";
import { renderInline } from "./StoryChapterBlock";

// Timed run-of-show. Items are a flat list; consecutive items sharing `part` are grouped
// into a jump-to section. `time` is 24h "HH:MM"; a live "Happening now" marker shows on event day.
const KIND = {
  welcome: { icon: "Hand", label: "Welcome" }, music: { icon: "Music", label: "Music" }, speaker: { icon: "Mic", label: "Speaker" },
  dance: { icon: "Sparkles", label: "Dance" }, ritual: { icon: "TreeDeciduous", label: "Peace Circle" }, youth: { icon: "Baby", label: "Youth" },
  keynote: { icon: "Star", label: "Keynote" }, memoriam: { icon: "Heart", label: "In memoriam" }, ceremony: { icon: "Flag", label: "Ceremony" },
  poem: { icon: "Feather", label: "Poem" }, break: { icon: "Utensils", label: "Grounds" }, parade: { icon: "Bird", label: "Parade" }, info: { icon: "Info", label: "" },
};
const mins = (t) => { const m = /^(\d{1,2}):(\d{2})/.exec(t || ""); return m ? Number(m[1]) * 60 + Number(m[2]) : null; };
const fmt = (t) => { const m = mins(t); if (m == null) return t || ""; const h = Math.floor(m / 60), mm = String(m % 60).padStart(2, "0"); return `${((h + 11) % 12) + 1}:${mm} ${h < 12 ? "am" : "pm"}`; };
const slug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const igEmbed = (u) => { const m = /instagram\.com\/(reel|p|tv)\/([\w-]+)/i.exec(u || ""); return m ? `https://www.instagram.com/${m[1]}/${m[2]}/embed/` : ""; };

const useNowMinutes = (eventDate, tz) => {
  const [now, setNow] = useState(null);
  useEffect(() => {
    const tick = () => {
      try {
        const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz || "America/Los_Angeles", hour12: false, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).formatToParts(new Date());
        const g = (k) => parts.find((p) => p.type === k)?.value;
        const today = `${g("year")}-${g("month")}-${g("day")}`;
        setNow(eventDate && today === eventDate ? Number(g("hour")) % 24 * 60 + Number(g("minute")) : null);
      } catch { setNow(null); }
    };
    tick(); const id = setInterval(tick, 60000); return () => clearInterval(id);
  }, [eventDate, tz]);
  return now;
};

const Item = ({ it, live, accent, onZoom }) => {
  const k = KIND[it.kind] || KIND.info;
  const Icon = Icons[k.icon] || Icons.Dot;
  const yt = it.embed_url ? embedSrcFrom(it.embed_url) : "";
  const ig = igEmbed(it.instagram_url);
  const img = it.image ? absUrl(it.image) : "";
  return (
    <li className={`relative pl-8 md:pl-10 py-4 ${live ? "bg-[#F2CC8F]/15 -mx-4 px-12 md:px-14 rounded-2xl" : ""}`} data-testid={`program-item-${slug(it.time)}`}>
      <span className="absolute left-0 md:left-1 top-6 h-4 w-4 rounded-full border-2 border-white shadow" style={{ backgroundColor: live ? "#E76F51" : accent }} />
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-mono text-sm font-bold tracking-tight" style={{ color: accent }}>{fmt(it.time)}</span>
        {live && <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#E76F51] animate-pulse" data-testid="program-live">● Happening now</span>}
        {k.label && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C5C5C]"><Icon className="h-3 w-3" /> {k.label}</span>}
      </div>
      <h4 className="font-['Playfair_Display'] text-xl md:text-2xl font-semibold text-[#0B2545] mt-1 leading-tight">{renderInline(it.title, accent)}</h4>
      {it.who && <p className="text-sm md:text-base text-[#3a3a3a] mt-0.5">{renderInline(it.who, accent)}</p>}
      {it.note && <p className="text-sm text-[#5C5C5C] mt-1 leading-relaxed">{renderInline(it.note, accent)}</p>}
      {it.link && (/^https?:/i.test(it.link)
        ? <a href={it.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-bold mt-2 underline-offset-2 hover:underline" style={{ color: accent }}>{it.link_label || "Learn more"} <Icons.ExternalLink className="h-3 w-3" /></a>
        : <Link to={it.link} className="inline-flex items-center gap-1 text-xs font-bold mt-2 underline-offset-2 hover:underline" style={{ color: accent }}>{it.link_label || "Learn more"} <Icons.ArrowRight className="h-3 w-3" /></Link>)}
      {(yt || ig || img) && (
        <div className="mt-4 grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto] items-start">
          {yt && <div className="rounded-2xl overflow-hidden shadow-lg ring-1 ring-black/10 bg-black aspect-video max-w-2xl" data-testid="program-embed-video"><iframe src={yt} title={it.title} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen loading="lazy" /></div>}
          {ig && <div className="rounded-2xl overflow-hidden shadow-lg ring-1 ring-black/10 bg-white w-full max-w-[360px]" data-testid="program-embed-instagram"><iframe src={ig} title={`${it.title} — Instagram`} className="w-full" style={{ height: 620 }} loading="lazy" allowTransparency="true" frameBorder="0" scrolling="no" /></div>}
          {img && <button type="button" onClick={() => onZoom(img)} className="rounded-2xl overflow-hidden shadow-lg ring-1 ring-black/10 cursor-zoom-in max-w-[320px]" data-testid="program-item-photo"><img src={img} alt={it.title} loading="lazy" className="w-full object-cover" /></button>}
          {it.embed_caption && <p className="sm:col-span-2 text-xs italic text-[#5C5C5C]">{renderInline(it.embed_caption, accent)}</p>}
        </div>
      )}
    </li>
  );
};

const ProgramBlock = ({ block }) => {
  const cfg = block.config || {};
  const accent = cfg.accent || "#1d4ed8";
  const items = (cfg.items || []).filter((i) => i.title);
  const now = useNowMinutes(cfg.event_date, cfg.timezone);
  const [zoom, setZoom] = useState(null);
  const groups = useMemo(() => items.reduce((acc, it) => {
    const last = acc[acc.length - 1];
    if (last && last.part === (it.part || "")) last.items.push(it); else acc.push({ part: it.part || "", items: [it] });
    return acc;
  }, []), [items]);
  const liveIdx = useMemo(() => {
    if (now == null) return -1;
    let idx = -1;
    items.forEach((it, i) => { const m = mins(it.time); if (m != null && m <= now) idx = i; });
    const endM = mins(cfg.end_time); return endM != null && now >= endM ? -1 : idx;
  }, [items, now, cfg.end_time]);
  if (!items.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-10" data-testid="fest-program">
      <BlockShell block={block} accent={accent}>
        {cfg.intro && <p className="text-base md:text-lg text-[#3a3a3a] leading-relaxed max-w-3xl mb-6">{renderInline(cfg.intro, accent)}</p>}
        <nav className="sticky top-16 z-20 -mx-6 md:mx-0 px-6 md:px-0 py-2 bg-[#F9F8F6]/90 backdrop-blur flex gap-2 overflow-x-auto no-scrollbar" aria-label="Jump to a part of the program" data-testid="program-jump-nav">
          {groups.map((g) => (
            <a key={g.part} href={`#program-${slug(g.part) || "start"}`} className="shrink-0 rounded-full px-4 py-1.5 text-xs font-bold text-white shadow hover:opacity-90 transition-opacity" style={{ backgroundColor: accent }} data-testid={`program-jump-${slug(g.part)}`}>
              {g.part || "Program"} <span className="opacity-70 font-mono">{fmt(g.items[0].time)}</span>
            </a>
          ))}
        </nav>
        <div className="mt-6 space-y-10">
          {groups.map((g) => {
            const start = items.indexOf(g.items[0]);
            return (
              <div key={g.part} id={`program-${slug(g.part) || "start"}`} className="scroll-mt-32" data-testid={`program-part-${slug(g.part)}`}>
                {g.part && (
                  <div className="flex items-center gap-4 mb-2">
                    <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl font-semibold text-[#0B2545]">{g.part}</h3>
                    <span className="h-px flex-1 bg-[#0B2545]/15" />
                    <span className="font-mono text-xs text-[#5C5C5C]">{fmt(g.items[0].time)}{g.items.length > 1 ? ` – ${fmt(g.items[g.items.length - 1].time)}` : ""}</span>
                  </div>
                )}
                <ol className="relative ml-2 md:ml-3 border-l-2 border-dashed" style={{ borderColor: `${accent}55` }}>
                  {g.items.map((it, i) => <Item key={i} it={it} live={start + i === liveIdx} accent={accent} onZoom={setZoom} />)}
                </ol>
              </div>
            );
          })}
        </div>
        {cfg.footnote && <p className="mt-8 text-xs italic text-[#5C5C5C] max-w-3xl">{renderInline(cfg.footnote, accent)}</p>}
      </BlockShell>
      <Lightbox src={zoom} onClose={() => setZoom(null)} />
    </section>
  );
};

export default ProgramBlock;
